import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import ScanConsultantSummaryDTO from '@src/adapters/dto/scan/ScanConsultantSummaryDTO';
import { useFindScansUnreviewedLazy } from '@src/adapters/hooks/scans/useFindScansUnreviewed';
import { useMigrateScansLazy } from '@src/adapters/hooks/scans/useMigrateScans';
import { useAuth } from '@src/auth/hooks/useAuth';
import ConsultantName from '@src/components/user/ConsultantName';
import { Routes } from '@src/navigation/Routes';
import buildRouteWithQueryParams from '@src/navigation/RouteWithQueryParams';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

function ConsultantList() {
  const navigate = useNavigate();
  const { currentHospitalId } = useAuth();
  const { execute: getList } = useFindScansUnreviewedLazy();
  const { execute: migrate } = useMigrateScansLazy();
  const [summaries, setSummaries] = useState<ScanConsultantSummaryDTO[]>([]);

  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [recordCount, setRecordCount] = useState(0);

  useEffect(() => {
    getList({
      pageSize: rowsPerPage,
      currentPage,
      hospitalId: currentHospitalId,
    }).then((result) => {
      setSummaries(result.scans);
      setRecordCount(result.recordCount);
    });
  }, [getList, currentPage, rowsPerPage, currentHospitalId]);

  const handleRowClick = (summary: ScanConsultantSummaryDTO) => {
    const url = buildRouteWithQueryParams(Routes.ConsultantReview, {
      consultantId: summary.consultantId,
    });
    navigate(url);
  };

  const handleMigrate = (event: React.MouseEvent, consultantId: string) => {
    event.stopPropagation();
    migrate({ consultantId }).then(() => {
      setSummaries((prev) =>
        prev.filter((s) => s.consultantId !== consultantId)
      );
    });
  };

  return (
    <TableContainer component={Paper}>
      <Table
        aria-labelledby="tableTitle"
        size="small"
        aria-label="Contents table">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell component="th" key="uploadDate" align="left">
              Remaining
            </TableCell>
            <TableCell component="th" key="extraFiles" align="left">
              Extra files
            </TableCell>
            <TableCell component="th" key="doNotMigrate" align="left">
              Do Not Migrate
            </TableCell>
            <TableCell component="th" key="migrate" align="left">
              Migrate
            </TableCell>
            <TableCell component="th" key="total" align="left">
              Total
            </TableCell>
            <TableCell
              component="th"
              key="action"
              align="left"
              sx={{ width: '200px' }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {summaries.map((summary, index) => {
            return (
              <TableRow
                hover
                onClick={() => handleRowClick(summary)}
                role="checkbox"
                tabIndex={-1}
                key={index}
                sx={{ cursor: 'pointer' }}>
                <TableCell scope="row">
                  <ConsultantName id={summary.consultantId} />
                </TableCell>
                <TableCell scope="row">
                  <Typography variant="body2">{summary.unknown}</Typography>
                </TableCell>
                <TableCell scope="row">{summary.extraFile}</TableCell>
                <TableCell scope="row">{summary.doNotMigrate}</TableCell>
                <TableCell scope="row">{summary.migrate}</TableCell>
                <TableCell scope="row">
                  {summary.unknown +
                    summary.extraFile +
                    summary.doNotMigrate +
                    summary.migrate}
                </TableCell>
                <TableCell scope="row">
                  {summary.unknown === 0 && (
                    <Button
                      color="success"
                      variant="contained"
                      onClick={(event) =>
                        handleMigrate(event, summary.consultantId)
                      }>
                      Migrate Scans
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={recordCount}
        rowsPerPage={rowsPerPage}
        page={currentPage}
        onPageChange={(event: any, number: any) => setCurrentPage(number)}
        onRowsPerPageChange={(event: any) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setCurrentPage(0);
        }}
      />
    </TableContainer>
  );
}

export default ConsultantList;
